// Renders the current Google map view to a PNG without a screen-capture
// prompt: fetches Google Static Maps images for the same center/zoom/style
// (stitched when the on-screen map is wider than one 640px request), then
// draws the markers on top at the same size.

const TILE = 640;   // max Static Maps size (css px); requested at scale=2
const STEP = 600;   // stitch step — the 40px overlap hides the logo band of upper rows
const SCALE = 2;

export interface ExportPin { lat: number; lon: number }
export interface ExportView {
  apiKey: string;
  center: { lat: number; lng: number };
  zoom: number;
  width: number; // css px of the (square) map on screen
  styles: google.maps.MapTypeStyle[];
  pins: ExportPin[];
}

const worldPx = (lat: number, lon: number, zoom: number) => {
  const scale = 256 * 2 ** zoom;
  const s = Math.sin((lat * Math.PI) / 180);
  return { x: ((lon + 180) / 360) * scale, y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale };
};
const unproject = (x: number, y: number, zoom: number) => {
  const scale = 256 * 2 ** zoom;
  const lon = (x / scale) * 360 - 180;
  const n = Math.PI - 2 * Math.PI * (y / scale);
  return { lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))), lon };
};

function styleParams(styles: google.maps.MapTypeStyle[]): string {
  return styles.map(rule => {
    const parts = [`feature:${rule.featureType ?? 'all'}`, `element:${rule.elementType ?? 'all'}`];
    (rule.stylers ?? []).forEach(st => {
      Object.entries(st).forEach(([k, v]) => {
        const val = typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? `0x${v.slice(1)}` : String(v);
        parts.push(`${k}:${val}`);
      });
    });
    return `&style=${encodeURIComponent(parts.join('|'))}`;
  }).join('');
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Static Maps request failed'));
    img.src = url;
  });
}

export async function exportMapPng(v: ExportView): Promise<string> {
  const W = Math.max(1, Math.round(v.width));
  const n = Math.max(1, Math.ceil((W - (TILE - STEP)) / STEP));
  const styles = styleParams(v.styles);
  const c = worldPx(v.center.lat, v.center.lng, v.zoom);

  const jobs: Promise<{ img: HTMLImageElement; x: number; y: number }>[] = [];
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const x = i * STEP, y = j * STEP; // tile top-left in map css px
      const mid = unproject(c.x + (x + TILE / 2 - W / 2), c.y + (y + TILE / 2 - W / 2), v.zoom);
      const url = `https://maps.googleapis.com/maps/api/staticmap?center=${mid.lat},${mid.lon}&zoom=${v.zoom}&size=${TILE}x${TILE}&scale=${SCALE}&maptype=roadmap${styles}&key=${v.apiKey}`;
      jobs.push(loadImage(url).then(img => ({ img, x, y })));
    }
  }
  const tiles = await Promise.all(jobs);

  const canvas = document.createElement('canvas');
  canvas.width = W * SCALE;
  canvas.height = W * SCALE;
  const ctx = canvas.getContext('2d')!;
  tiles.forEach(t => ctx.drawImage(t.img, t.x * SCALE, t.y * SCALE)); // later tiles overdraw the overlap

  v.pins.forEach(p => {
    const w = worldPx(p.lat, p.lon, v.zoom);
    const px = (w.x - c.x + W / 2) * SCALE;
    const py = (w.y - c.y + W / 2) * SCALE;
    if (px < 0 || py < 0 || px > canvas.width || py > canvas.height) return;
    ctx.save();
    ctx.translate(px, py);
    ctx.scale(SCALE, SCALE);
    ctx.beginPath(); ctx.arc(0, 0, 10, 0, Math.PI * 2); ctx.fillStyle = 'rgba(15,107,68,0.28)'; ctx.fill(); // halo
    ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 3; ctx.shadowOffsetY = 1;
    ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.beginPath(); ctx.arc(0, 0, 5, 0, Math.PI * 2); ctx.fillStyle = '#0f6b44'; ctx.fill();
    ctx.restore();
  });

  return canvas.toDataURL('image/png');
}
